<template>
  <div class="insights-github-integration">
    <template v-if="hasData">
      <div
        id="gh-cards"
        class="cards"
        :style="cardStyle"
      >
        <div class="wrapper wrapper-wide">
          <InsightsGitHubIntegrationCard
            v-for="card in cards.wide"
            :key="card.key"
            v-track:click="{
              label: 'Widget click',
              widget_name: `GitHub ${card.title}`,
              datatype: 'Card',
            }"
            :card="card"
            type="wide"
            @click="onCardCLick(card)"
          >
            <template
              v-if="card.key === 'time_to_merge'"
              #duration
            >
              <div class="time-to-merge">
                <div class="metric">
                  <div class="title">Mean</div>
                  <div class="value">{{ formatValue(card.mean.value, card.mean.valueType) }}</div>
                  <InsightsMetric
                    v-if="card.mean.delta_percentage !== null"
                    type="percentage"
                    :value="card.mean.delta_percentage"
                  />
                </div>
                <div class="metric">
                  <div class="title">95th percentile</div>
                  <div class="value">{{ formatValue(card.percentale.value, card.percentale.valueType) }}</div>
                  <InsightsMetric
                    v-if="card.percentale.delta_percentage !== null"
                    type="percentage"
                    :value="card.percentale.delta_percentage"
                  />
                </div>
                <div class="description">{{ card.description }}</div>
              </div>
            </template>
            <template #footer>
              <template v-if="card.key === 'throughput_percentage'">
                <div class="widget-title">Pull requests</div>
                <div class="troughput-metrics">
                  <div
                    v-for="metric in card.additionalData"
                    :key="metric.title"
                    class="metric"
                  >
                    <div class="value">{{ metric.data.value }}</div>
                    <div>{{ metric.title }}</div>
                  </div>
                </div>
              </template>
            </template>
          </InsightsGitHubIntegrationCard>
        </div>
        <div class="wrapper wrapper-slim">
          <InsightsGitHubIntegrationCard
            v-for="card in cards.slim"
            :key="card.key"
            v-track:click="{
              label: 'Widget click',
              widget_name: `GitHub ${card.title}`,
              datatype: 'Card',
            }"
            :card="card"
            type="slim"
            @click="onCardCLick(card)"
          >
            <template #footer>
              {{ card.description }}
            </template>
          </InsightsGitHubIntegrationCard>
        </div>
      </div>
      <div
        v-if="!userId"
        v-track:click="{
          label: 'Widget click',
          widget_name: 'GitHub Contributors',
          datatype: 'Card',
        }"
        class="widget-wrapper widget-common mt-20"
      >
        <div class="widget-title d-flex justify-space-between">
          <div class="d-flex align-items-center">
            <span class="title-label mr-15">Contributors</span>
            <AppSwitch
              v-model="showMetrics"
              class="title-switch leaderboard-metrics mr-5 cursor-pointer"
              label="Trend data"
              text-position="right"
            />
            <i
              v-tooltip="tooltipOptions"
              class="hi hi-info-filled hi-12 a-tooltip leaderboard-metrics"
              title="Trend data is calculated using the selected time frame until the present date against the same period of the previous time frame."
            ></i>
          </div>
          <a
            class="insights-widget-view-more"
            @click="toggleContributorsDialog"
          >
            <i class="hi hi-external"></i>
            <span> View all </span>
          </a>
        </div>
        <InsightsGitHubIntegrationContributorsTable
          :table-data="tableData"
          :loading="loadingContributors"
          :sort-options="sortOptions"
          :show-metrics="showMetrics"
          @sort="sortContributors"
          @member-selected="selectMember"
        />
      </div>
      <InsightsGitHubIntegrationContributorsDialog
        v-if="showContributorsDialog"
        ref="contributorsDialog"
        class="insights-github-integration-dialog"
        :loading="loadingWidgets.git_hostings_table"
        :lazy-loading="!!lazyLoadingWidgets.git_hostings_table"
        :show-dialog="showContributorsDialog"
        :show-metrics="showMetrics"
        :sort-options="sortOptions"
        :table-data="dialogTableData"
        @sort="sortContributors"
        @close-dialog="toggleContributorsDialog"
        @member-selected="selectMember"
        @load-more-data="loadMoreData"
        @toggle-metrics="toggleMetrics"
      />
      <InsightsGitHubIntegrationCardsDialog
        v-if="showCardsDialog"
        class="insights-github-integration-dialog"
        :show-dialog="showCardsDialog"
        :card="currentDialogCard"
        @close-dialog="toggleCardsDialog"
      />
    </template>
    <InsightsNoData
      v-else
      display="card"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { mapActions, mapGetters } from 'vuex';
import { ICON_GEARS, IMAGE_EMPTY_BOX } from '~/rails_assets';
import { INSIGHTS } from '~/app_constants';
import { InsightsFilters } from '~/mixins';
import InsightsMetric from '~/components/insights/InsightsMetric.vue';
import InsightsGitHubIntegrationContributorsTable from '~/components/insights/InsightsGitHubIntegrationContributorsTable.vue';
import InsightsGitHubIntegrationCard from '~/components/insights/InsightsGitHubIntegrationCard.vue';
import InsightsGitHubIntegrationContributorsDialog from '~/components/insights/InsightsGitHubIntegrationContributorsDialog.vue';
import InsightsGitHubIntegrationCardsDialog from '~/components/insights/InsightsGitHubIntegrationCardsDialog.vue';
import InsightsNoData from '~/components/insights/InsightsNoData.vue';

dayjs.extend(duration);

export default {
  components: {
    InsightsGitHubIntegrationContributorsTable,
    InsightsGitHubIntegrationCard,
    InsightsGitHubIntegrationContributorsDialog,
    InsightsGitHubIntegrationCardsDialog,
    InsightsMetric,
    InsightsNoData,
  },
  mixins: [InsightsFilters],
  data: () => ({
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    showMetrics: true,
    integrationAPI: INSIGHTS.api.gitHubIntegration,
    resizeObserver: null,
    resizeTimeout: null,
    widgetWidth: null,
    ICON_GEARS,
    IMAGE_EMPTY_BOX,
    showCardsDialog: false,
    showContributorsDialog: false,
    widgetLimit: 10,
    limit: 20,
    currentSortOption: {},
    currentDialogCard: {},
  }),
  computed: {
    ...mapGetters(['gitHosting', 'gitHostingTable', 'loadingWidgets', 'lazyLoadingWidgets', 'userId']),
    cards() {
      return {
        wide: [
          {
            key: 'throughput_percentage',
            valueType: 'percentage',
            title: 'throughput',
            widgetType: 'wide',
            description:
              'The ratio of PR created to PR merged. It shows the ability to finish work started in a certain period. A lower number may identify bottlenecks in the development process.',
            value: this.gitHosting?.throughput_percentage.value,
            delta: this.gitHosting?.throughput_percentage.delta,
            additionalData: [
              {
                title: 'Created',
                data: this.gitHosting?.prs_created,
              },
              {
                title: 'Merged',
                data: this.gitHosting?.prs_merged,
              },
              {
                title: 'Abandoned',
                data: this.gitHosting?.prs_abandoned,
              },
            ],
          },
          {
            key: 'time_to_merge',
            widgetType: 'wide',
            title: 'time to merge(TTM)',
            description:
              'The typical time it takes for most PRs to go from opened to merged within the selected time period. Excluding extreme values or outliers by looking at the 95th percentile to get a better idea of “normal”.',
            mean: {
              value: this.gitHosting?.time_to_merge.value,
              delta: this.gitHosting?.time_to_merge.delta,
              delta_percentage: this.gitHosting?.time_to_merge.delta_percentage,
              valueType: 'duration',
            },
            percentale: {
              value: this.gitHosting?.time_to_merge_95.value,
              delta: this.gitHosting?.time_to_merge_95.delta,
              delta_percentage: this.gitHosting?.time_to_merge_95.delta_percentage,
              valueType: 'duration',
            },
            value: true, // to prevent the no-data state.
          },
        ],
        slim: [
          {
            key: 'review_requests',
            widgetType: 'slim',
            title: 'median review',
            description: 'Median number of reviews on pull requests with 1+ commits.',
            value: this.gitHosting?.review_requests.value,
            delta: this.gitHosting?.review_requests.delta,
            valueType: 'strict_count',
          },
          {
            key: 'time_to_first_review',
            widgetType: 'slim',
            title: 'mean time to first review',
            description:
              'Mean time pull requests wait before receiving a review (approval or comment). Excluding contributors with zero reviews.',
            value: this.gitHosting?.time_to_first_review.value,
            delta: this.gitHosting?.time_to_first_review.delta,
            valueType: 'duration',
          },
          {
            key: 'context_switching',
            widgetType: 'slim',
            title: 'context switching',
            description: 'How many PRs in parallel a dev is working on, in the selected time period.',
            value: this.gitHosting?.context_switching.value,
            delta: this.gitHosting?.context_switching.delta,
            valueType: 'count',
          },
          {
            key: 'review_turnaround',
            widgetType: 'slim',
            title: 'review turnaround',
            description:
              'The time between review requested and review given for each review, not just the first pull request.',
            value: this.gitHosting?.review_turnaround.value,
            delta: this.gitHosting?.review_turnaround.delta,
            valueType: 'duration',
          },
        ],
      };
    },
    cardStyle() {
      return {
        gridTemplateColumns: this.widgetWidth > 640 ? '1fr 1fr' : '1fr',
      };
    },
    loadingContributors() {
      return !this.showContributorsDialog && this.loadingWidgets.git_hostings_table;
    },
    tableData() {
      return this.gitHostingTable.data.slice(0, this.widgetLimit);
    },
    dialogTableData() {
      return this.gitHostingTable.data;
    },
    offset() {
      return this.dialogTableData?.length || 0;
    },
    sortOptions() {
      return this.gitHostingTable.sort || {};
    },
    isFullyLoaded() {
      return !!this.gitHostingTable.isFullyLoaded;
    },
    hasData() {
      return !!(this.gitHostingTable && this.gitHostingTable.data && this.gitHostingTable.data.length);
    },
  },
  mounted() {
    this.setupResizeObserver();
  },
  beforeUnmount() {
    this.stopResizeObserver();
  },
  methods: {
    ...mapActions(['fetchWidgets', 'setFullyLoaded']),
    onResize(width) {
      this.widgetWidth = width;
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.onResize(entries[0].target.clientWidth);
        }, 0);
      });

      this.resizeObserver.observe(document.getElementById('gh-cards'));
    },
    formatValue(value, type) {
      if (value === null) {
        return '-';
      }

      switch (type) {
        case 'strict_count':
          return Math.floor(value);
        case 'count':
          return Math.floor(value);
        case 'percentage':
          return `${Math.floor(value)}%`;
        case 'duration':
          // eslint-disable-next-line no-case-declarations
          const duration = dayjs.duration(value, 'seconds');

          return `${duration.days() > 0 ? `${duration.days()}d` : ''} ${duration.hours()}h`;
        default:
          return value || '-';
      }
    },
    async onCardCLick(currentCard) {
      this.showCardsDialog = true;
      this.currentDialogCard = currentCard;
    },
    toggleCardsDialog() {
      this.showCardsDialog = !this.showCardsDialog;
    },
    toggleContributorsDialog() {
      this.showContributorsDialog = !this.showContributorsDialog;
    },
    selectMember(row) {
      const string = `${row.id}_user_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string, row.time_zone?.for);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    setLoadedState(value = 0) {
      const state = !(
        this.gitHostingTable.data.length % this.limit === 0 && this.gitHostingTable.data.length !== value
      );

      this.setFullyLoaded({ widgetName: this.integrationAPI.table, value: state });
    },
    sortContributors(options) {
      if (this.$refs.contributorsDialog) {
        this.$refs.contributorsDialog.scrollToTop();
      }

      this.currentSortOption = {
        sort_by: options.field,
        sort_order: options.direction,
      };

      this.fetchWidgets({
        widgetKey: this.integrationAPI.table,
        extraQueryParams: {
          ...this.currentSortOption,
          integration_type: 'github',
          limit: this.limit,
        },
      });
    },
    toggleMetrics(value) {
      this.showMetrics = !!value;
    },
    async loadMoreData() {
      if (this.hasData && !this.isFullyLoaded) {
        const dataLength = this.gitHostingTable.data.length;

        await this.fetchWidgets({
          lazy: true,
          widgetKey: this.integrationAPI.table,
          extraQueryParams: {
            offset: this.offset,
            integration_type: 'github',
            ...this.currentSortOption,
          },
          persists: true,
        });

        this.setLoadedState(dataLength);
      }
    },
  },
};
</script>
